<script setup lang="ts">
import type { Album } from "@/models"
import { toTypedSchema } from "@vee-validate/zod"
import { useForm } from "vee-validate"
import { computed, ref, watch } from "vue"
import { z } from "zod"
import { useUnlockAlbum } from "@/api"
import { BaseModal, Button, Card, Icon, Input } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useModal, useTranslation } from "@/composables"
import { ValidationException } from "@/http"
import { GuestUnlockAlbumModalKey } from "@/symbols"

interface GuestUnlockAlbumModalProps {
  eventVaultId?: number
  album?: Album
}

const props = withDefaults(defineProps<GuestUnlockAlbumModalProps>(), {
  eventVaultId: undefined,
  album: undefined,
})

const emit = defineEmits<{
  (e: "unlocked"): void
  (e: "close"): void
}>()
const eventVaultId = computed(() => props.eventVaultId)
const albumId = computed(() => props.album?.id)

const { t } = useTranslation()

const toast = useToast()

const { toggle, isActive } = useModal(GuestUnlockAlbumModalKey)

const validationErrors = ref()

const validationSchema = computed(() =>
  toTypedSchema(
    z.object({
      passcode: z.string().min(1, t("unlock_album_page.error.passcode")),
    }),
  ),
)

const { handleSubmit, resetForm } = useForm({
  validationSchema,
  initialValues: {
    passcode: "",
  },
})

const { mutate, isPending } = useUnlockAlbum()

const onSubmit = handleSubmit(({ passcode }) => {
  if (!eventVaultId.value || !albumId.value) {
    toast.error()
    return
  }

  validationErrors.value = undefined
  mutate({ passcode, eventVaultId: eventVaultId.value, albumId: albumId.value }, {
    onSuccess: () => {
      emit("unlocked")
      toggle()
    },
    onError: (error) => {
      let errorMessage = t("check_details_try_again")
      if (error instanceof ValidationException) {
        errorMessage = t("validation_error")
        validationErrors.value = error
      }
      toast.error({ description: errorMessage })
    },
  })
})

watch(isActive, (active) => {
  validationErrors.value = undefined
  resetForm({ values: { passcode: "" } })
  if (!active)
    emit("close")
})
</script>

<template>
  <BaseModal :name="GuestUnlockAlbumModalKey" data-test="guest-unlock-album-modal">
    <Card class="mx-auto w-full max-w-md guest-layout">
      <template #header>
        <div class="mb-4 flex items-center justify-between gap-4">
          <div class="flex min-w-0 items-start gap-3">
            <Icon icon="FolderLock" class="size-10 shrink-0 text-primary" />
            <div class="min-w-0">
              <h2 class="font-comfortaa text-xl leading-tight font-semibold">
                {{ t("unlock_album_page.title") }}
              </h2>
              <p v-if="album?.title" class="mt-1 truncate text-sm text-gray-600">
                {{ album.title }}
              </p>
            </div>
          </div>
          <Button
            variant="flat"
            type="button"
            prefix-icon="Close"
            class="-mt-6 -mr-4"
            data-test="guest-unlock-album-modal-dismiss"
            @click="toggle"
          >
            <span class="sr-only">{{ t("cancel") }}</span>
          </Button>
        </div>
      </template>
      <form class="flex flex-col gap-4" @submit.prevent="onSubmit">
        <p class="text-sm text-gray-700">
          {{ t("unlock_album_page.message") }}
        </p>
        <Input
          :disabled="isPending"
          name="passcode"
          :label="t('unlock_album_page.form.passcode')"
          type="password"
          data-test="guest-unlock-album-passcode"
        />
        <Button :disabled="isPending" size="lg" type="submit" data-test="guest-unlock-album-submit">
          {{ t("submit") }}
        </Button>
      </form>
    </Card>
  </BaseModal>
</template>
